/* eslint-disable react-dom/no-missing-iframe-sandbox */
/* eslint-disable no-alert */
// import { format } from "date-fns";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import AudioPlayer from "react-h5-audio-player";
import { useTranslation } from "react-i18next";
import { FaRegTrashAlt } from "react-icons/fa";

import { useUIStateStore } from "@/store/uiStateStore";

import "react-h5-audio-player/lib/styles.css";

import { useDeleteRecording } from "../../../../api/mutations";
import {
  useGetMyAccount,
  useGetMyRecordingsDetailed,
  useGetRecordingMetas,
} from "../../../../api/queries";
import { hasAdminRights } from "../../../../auth/shared";
import LoadingSmall from "../../../../components/LoadingSmall";
import Modal from "../../../../components/Modal";
import RebrandPrimaryButton from "../../../../components/RebrandPrimaryButton";
// import AppState from "../../../../store/appstate";
import useAppStore from "../../../../store/appStateZustand";
import DeleteRecordingModal from "./DeleteRecordingModal";
import RecordingAttachmentForm from "./RecordingAttachmentForm";

function SongRecording({ recording, last }) {
  const recordingMetas = useGetRecordingMetas({ id: recording.workcode });
  // const selectedAccount = AppState.useState(s => s.selectedAccount);
  const selectedAccount = useAppStore(s => s.selectedAccount);
  const myAccount = useGetMyAccount({
    artistaccountcode: selectedAccount,
  });
  const adminRights = hasAdminRights(myAccount?.data);
  const { t } = useTranslation("songs");

  const isTunecore = useUIStateStore(state => state.isTunecore);
  const queryClient = useQueryClient();
  const deleteRecordingMutation = useDeleteRecording();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const [recordingToDelete, setRecordingToDelete] = useState(null);

  let recordingInfo = null;

  if (recordingMetas.isSuccess) {
    recordingInfo = recordingMetas?.data?.find(
      r =>
        r.id === recording.recordingidentifier || r.id === recording.uicbarcode,
    );
  }

  const attachMp3lbl = recording.s3presignedurl
    ? t("songs:attach-new-mp3")
    : t("songs:attach-mp3");

  const [recordingAttachmentModalIsOpen, setRecordingAttachmentModalIsOpen] = useState(false);
  const [mp3PlayerModalIsOpen, setMp3PlayerModalIsOpen] = useState(false);

  const recordings = useGetMyRecordingsDetailed({
    aac: recording.artistaccountcode || selectedAccount,
    wc: recording.workcode,
  });

  const [recordingMp3Url, setRecordingMp3Url] = useState("");
  const [recordingAttachmentModal, setRecordingAttachmentModal] = useState(null);

  const handleConfirmDelete = (rec = recordingToDelete) => {
    if (!rec) {
      console.warn("No recording selected for deletion.");
      return;
    }
    setIsDeleting(true);

    deleteRecordingMutation.mutate(
      {
        artistaccountcode: rec.artistaccountcode,
        recordingidentifier: rec.recordingidentifier,
        recordingamendmentidentifier: rec.recordingamendmentidentifier,
        workcode: rec.workcode,
        // isdeleted: true,
      },
      {
        onSuccess: () => {
          setShowDeleteModal(false);
          setRecordingToDelete(null);
          queryClient.invalidateQueries({ queryKey: ["recordings"] });
          recordings.refetch();
          setIsDeleted(true);
        },
        onError: (error) => {
          console.error("Failed to delete recording:", error);
          alert(`Failed to delete recording: ${error?.message || error}`);
          setShowDeleteModal(false);
          setRecordingToDelete(null);
          setIsDeleting(false);
        },
      },
    );
  };

  const handleDeleteRecordingClick = () => {
    const showWarning = localStorage.getItem("showDeleteRecordingWarning");
    if (showWarning === "false") {
      // Delete immediately by passing the recording directly.
      handleConfirmDelete(recording);
    }
    else {
      // Set the recording to state and show the modal.
      setRecordingToDelete(recording);
      setShowDeleteModal(true);
    }
  };

  return (
    <>
      <DeleteRecordingModal
        isOpen={showDeleteModal}
        onConfirm={handleConfirmDelete}
        onClose={() => setShowDeleteModal(false)}
      />
      <Modal
        smallModal
        openPortal={recordingAttachmentModalIsOpen}
        closePortal={setRecordingAttachmentModalIsOpen}
      >
        {recordingAttachmentModal !== null && (
          <RecordingAttachmentForm
            artistaccountcode={recording.artistaccountcode}
            recordingamendmentidentifier={
              recording.recordingamendmentidentifier
            }
            recordingidentifier={recording.recordingidentifier}
            workcode={recording.workcode}
            modal
            closeModal={setRecordingAttachmentModalIsOpen}
          />
        )}
      </Modal>

      <Modal
        smallModal
        openPortal={mp3PlayerModalIsOpen}
        closePortal={() => setMp3PlayerModalIsOpen(false)}
      >
        <div className="my-16 audioContainer">
          <div className="flex flex-col items-center gap-6 mb-4 md:flex-row">
            <div className="stockImage w-fit" />
            <div className="flex flex-col items-center gap-4 md:items-start">
              {recording.title && (
                <p className="font-bold md:text-xl text-[#AFB8BF]">
                  {recording.title}
                </p>
              )}
              {recording.performingartist && (
                <p className="font-medium italic text-[#6D7281]">
                  {recording.performingartist}
                </p>
              )}
            </div>
          </div>

          {recordingMp3Url && <AudioPlayer autoPlay src={recordingMp3Url} />}
        </div>
      </Modal>

      {isDeleting || isDeleted ? (
        <div className="flex items-center justify-center w-full h-20">
          <LoadingSmall />
        </div>
      ) : (
        <div
          className={`flex flex-wrap items-center py-2  ${
            last ? "" : "border-b border-old-sentric-menu-gray"
          } `}
        >
          {(recordingInfo?.spotify_id || recordingInfo?.mp3) && (
            <div className="w-full h-20 mb-3 ">
              {recordingInfo?.spotify_id && (
                <iframe
                  title="preview"
                  src={`https://open.spotify.com/embed/track/${recordingInfo.spotify_id}`}
                  className="w-full"
                  height="80"
                  allow="encrypted-media"
                  scrolling="no"
                  style={{ borderRadius: "15px" }}
                />
              )}
            </div>
          )}
          <div style={{ flexGrow: 1 }} className="flex flex-col gap-2">
            <h2
              className={`${
                isTunecore ? "text-white" : "text-black font-medium mb-2"
              } text-base font-bold`}
            >
              {" "}
              {recording.title}
              {" - "}
              {recording.ident}
              {" - "}
              {recording.performingartist}
            </h2>
            <div
              className={`text-xs flex flex-col gap-2 sm:gap-0 ${
                isTunecore ? "text-white" : "text-sentric-rebrand-grey-text"
              }`}
            >
              <p>
                ISRC:
                {recording.isrc || "-"}
              </p>
              <p>
                {t("songs:release-date")}
                :
                {" "}
                {recording.releasedate && recording.releasedate !== "0000-00-00"
                  ? recording.releasedate.split("T")[0]
                  : " - "}
              </p>

              <p>
                {t("songs:record-label-caps")}
                :
                {recording.recordlabel || "-"}
              </p>
            </div>
          </div>
          <div className="flex flex-col items-start gap-2 mt-4 md:flex-row xl:mt-0 lg:justify-end xl:items-end ">
            <RebrandPrimaryButton
              className=""
              onClick={() => {
                setRecordingAttachmentModal({
                  recordingidentifier: recording.recordingidentifier,
                  recordingamendmentidentifier: recording.recordingamendmentidentifier,
                  workcode: recording.workcode,
                });
                setRecordingAttachmentModalIsOpen(true);
              }}
              disabled={!adminRights || isDeleting}
              arrow={!isTunecore}
            >
              {attachMp3lbl}
            </RebrandPrimaryButton>

            {recording?.s3presignedurl && (
              <RebrandPrimaryButton
                className=""
                onClick={() => {
                  setRecordingMp3Url(recording.s3presignedurl);
                  setMp3PlayerModalIsOpen(true);
                }}
                disabled={!adminRights || isDeleting}
                arrow={!isTunecore}
              >
                {t("songs:play-mp3")}
              </RebrandPrimaryButton>
            )}
            <RebrandPrimaryButton
              onClick={handleDeleteRecordingClick}
              disabled={!adminRights || isDeleting}
              className="inline-flex items-center "
            >
              {t("common:delete", "Delete")}
              <FaRegTrashAlt className="mb-1 ml-3" />
            </RebrandPrimaryButton>
          </div>
        </div>
      )}
    </>
  );
}

export default SongRecording;
